/**
 * KAN-752: страница оператора /users/:id вместо модалки редактирования.
 *
 * Вкладки (в URL: ?tab=): "Основное" (поля пользователя), "Инструкции"
 * (части инструкций с превью и материалы), "Приветствия A/B" (варианты и
 * статистика этого оператора), "Каналы", "Стадии воронки" (если включена
 * адаптация), "Telegram" (канал telegram_user с включением и ссылка на аккаунты). Основное и Инструкции - одна
 * форма (useUserEditForm), сохраняются тем же PATCH /users/:id/.
 * Права как у прежнего диалога: редактирует владелец кабинета.
 * Уход со страницы с несохранёнными правками спрашивает подтверждение
 * (useUnsavedChangesGuard), как confirm прежнего диалога.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { PillTabs, type PillTab } from '@/components/dashboard/PillTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChannelsPanel } from '@/components/users/ChannelsPanel';
import { StagePromptsSection } from '@/components/users/StagePromptsSection';
import { OperatorInstructionsFields } from '@/components/users/OperatorInstructionsFields';
import { UserFormError, UserMainFields } from '@/components/users/UserMainFields';
import { GreetingVariantsBlock } from '@/components/telegram/GreetingVariantsBlock';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUser } from '@/hooks/useUsers';
import { useUserEditForm, type UserEditForm } from '@/hooks/useUserEditForm';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { USERS_QUERY_KEY } from '@/services/api/queryKeys';

export type UserDetailTab = 'main' | 'instructions' | 'greetings' | 'channels' | 'stages' | 'telegram';

const DEFAULT_TAB: UserDetailTab = 'main';
const BREADCRUMBS_ROOT = { label: 'Пользователи', href: '/users' };
export const TELEGRAM_ACCOUNTS_HREF = '/settings?tab=telegram';
/** KAN-752: канал рассылки с личных Telegram-аккаунтов (у новой кампании выключен). */
const TELEGRAM_OUTREACH_CHANNELS = ['telegram_user'] as const;

function tabsFor(withStages: boolean): PillTab<UserDetailTab>[] {
  const tabs: PillTab<UserDetailTab>[] = [
    { value: 'main', label: 'Основное' },
    { value: 'instructions', label: 'Инструкции' },
    { value: 'greetings', label: 'Приветствия A/B' },
    { value: 'channels', label: 'Каналы' },
  ];
  if (withStages) tabs.push({ value: 'stages', label: 'Стадии воронки' });
  tabs.push({ value: 'telegram', label: 'Telegram' });
  return tabs;
}

function SaveBar({ form, testId }: { form: UserEditForm; testId: string }): JSX.Element {
  return (
    <div className="mt-6 flex items-center gap-3 border-t pt-4">
      <Button
        type="submit"
        data-testid={testId}
        disabled={form.isPending || !form.isDirty || !form.isOperatorValid}>
        {form.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {form.isPending ? 'Сохранение...' : 'Сохранить'}
      </Button>
      {form.isDirty && (
        <span data-testid="unsaved-note" className="text-xs text-amber-700">
          Есть несохранённые изменения
        </span>
      )}
    </div>
  );
}

function BackButton(): JSX.Element {
  return (
    <Button variant="outline" asChild>
      <Link to="/users" data-testid="back-to-users">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Назад к пользователям
      </Link>
    </Button>
  );
}

function PageMessage({ testId, children }: { testId: string; children: React.ReactNode }): JSX.Element {
  return (
    <PageLayout title="Пользователь" breadcrumbs={[BREADCRUMBS_ROOT]} actions={<BackButton />}>
      <div className="py-12 text-center text-sm text-gray-600" data-testid={testId}>
        {children}
      </div>
    </PageLayout>
  );
}

export function UserDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: currentUser, isLoading: meLoading } = useCurrentUser();
  const isOwner = currentUser?.role === 'OWNER';
  const { data: user, isLoading, isError } = useUser(isOwner ? id : '');

  const onSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
    toast.success('Сохранено');
  }, [queryClient]);
  const form = useUserEditForm(user ?? null, { onSaved });
  useUnsavedChangesGuard(form.isDirty && !form.isPending);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo(() => tabsFor(form.useStagePrompts), [form.useStagePrompts]);
  const requested = searchParams.get('tab');
  const tab: UserDetailTab = tabs.some(t => t.value === requested)
    ? (requested as UserDetailTab)
    : DEFAULT_TAB;
  const setTab = useCallback(
    (next: UserDetailTab) =>
      setSearchParams(next === DEFAULT_TAB ? {} : { tab: next }, { replace: true }),
    [setSearchParams]
  );

  const title = user?.full_name?.trim() || user?.email || 'Пользователь';
  useEffect(() => {
    document.title = `${title} - SMETA AI`;
  }, [title]);

  if (meLoading || (isOwner && isLoading)) {
    return (
      <PageMessage testId="user-loading">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
      </PageMessage>
    );
  }
  if (!isOwner) {
    return <PageMessage testId="user-forbidden">Редактировать пользователей может владелец кабинета</PageMessage>;
  }
  if (isError || !user) {
    return <PageMessage testId="user-error">Пользователь не найден</PageMessage>;
  }

  const isCurrentUser = currentUser?.id === user.id;

  return (
    <PageLayout
      title={title}
      titleAddon={
        user.is_campaign_operator ? (
          <Badge variant="outline" data-testid="campaign-badge">
            Кампания
          </Badge>
        ) : undefined
      }
      breadcrumbs={[{ label: 'Главная', href: '/' }, BREADCRUMBS_ROOT, { label: title }]}
      actions={<BackButton />}
      data-testid="user-detail-page">
      <div className="space-y-4">
        <PillTabs label="Разделы пользователя" tabs={tabs} value={tab} onChange={setTab} testIdPrefix="user-tab" />

        <div className="rounded-lg border bg-white p-4 sm:p-6">
          {tab === 'main' && (
            <form onSubmit={form.submit} data-testid="user-main-form" className="max-w-2xl">
              <UserFormError message={form.formError} />
              <UserMainFields
                user={user}
                form={form}
                isCurrentUser={isCurrentUser}
                stagesHint='Тексты стадий настраиваются на вкладке "Стадии воронки".'
              />
              <SaveBar form={form} testId="save-main-button" />
            </form>
          )}

          {tab === 'instructions' &&
            (form.isSouffleur ? (
              <p data-testid="instructions-souffleur-note" className="text-sm text-gray-600">
                Инструкции задаются для шаблонов, которые общаются с клиентом. Выберите такой шаблон
                на вкладке "Основное".
              </p>
            ) : (
              <form onSubmit={form.submit} data-testid="user-instructions-form" className="max-w-3xl space-y-4">
                <UserFormError message={form.formError} />
                <OperatorInstructionsFields user={user} form={form} />
                <SaveBar form={form} testId="save-instructions-button" />
              </form>
            ))}

          {tab === 'greetings' && <GreetingVariantsBlock operatorId={user.id} />}

          {tab === 'channels' && <ChannelsPanel user={user} self={isCurrentUser} />}

          {tab === 'stages' && <StagePromptsSection useStage userId={user.id} />}

          {tab === 'telegram' && (
            <div data-testid="user-telegram-tab" className="space-y-3 text-sm text-gray-700">
              <p data-testid="telegram-tab-hint">
                Включите канал и добавьте аккаунты рассылки этой кампании. Аккаунты подключаются и
                привязываются к оператору на странице Telegram-аккаунтов, там же лимиты отправки,
                прокси и статус подключения.
              </p>
              <ChannelsPanel user={user} self={isCurrentUser} only={TELEGRAM_OUTREACH_CHANNELS} />
              <Button variant="outline" asChild>
                <Link to={TELEGRAM_ACCOUNTS_HREF} data-testid="telegram-accounts-link">
                  Открыть Telegram-аккаунты
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export default UserDetailPage;
